import { InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import { CancelOutlined } from '@mui/icons-material';
import { Button, CircularProgress, Dialog, Grid, MenuItem, TextField, TextFieldProps } from '@mui/material';
import { WidgetContent, WidgetHeader, WidgetIcon, useCustomSnackbar } from '@procureai/ui-mui';
import { PropsWithChildren, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { TsrctCaseCancellationReason } from '@/generated/gql/graphql';
import { CANCEL_SOURCING_CASE_MUTATION } from '@/legacy/routes/autonomousSourcing/SourcingCaseDetail/SourcingCaseDetail.gql';
import { Cover } from '~/components/Cover/Cover';

const commonInputProps: Partial<TextFieldProps> = {
  margin: 'dense',
  fullWidth: true,
  size: 'small',
  variant: 'outlined',
};

const REASON_OPTIONS: TsrctCaseCancellationReason[] = [
  'NO_LONGER_NEEDED' as TsrctCaseCancellationReason,
  'BUDGET_CONSTRAINT' as TsrctCaseCancellationReason,
  'SUPPLIER_UNAVAILABLE' as TsrctCaseCancellationReason,
  'DUPLICATE_CASE' as TsrctCaseCancellationReason,
  'REQUIREMENTS_CHANGED' as TsrctCaseCancellationReason,
  'AWARDED_OUTSIDE_PLATFORM' as TsrctCaseCancellationReason,
  'OTHER' as TsrctCaseCancellationReason,
];

interface FormData {
  reason: TsrctCaseCancellationReason | null;
  message: Union.Nullable<string>;
}

export interface SourcingCaseCancelDialogProps {
  sourcingCaseId: string;
  open: boolean;
  onClose: () => void;
  onCancelled?: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
}

export const SourcingCaseCancelDialog = ({
  sourcingCaseId,
  open,
  onClose,
  onCancelled,
  refetchQueries,
}: PropsWithChildren<SourcingCaseCancelDialogProps>) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SourcingCaseCancelDialog' });
  const { t: tCommon } = useTranslation();
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const [formData, setFormData] = useState<FormData>({ reason: null, message: null });

  const [cancelSourcingCase, { loading }] = useMutation(CANCEL_SOURCING_CASE_MUTATION, {
    onError,
    refetchQueries,
  });

  const handleApply = async () => {
    const { errors } = await cancelSourcingCase({
      variables: {
        id: sourcingCaseId,
        data: { reason: formData.reason, message: formData.message },
      },
    });
    if (errors) return;
    enqueueSuccessSnackbar(t('success'));
    onCancelled?.();
    onClose();
  };

  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      reason: value ? (value as TsrctCaseCancellationReason) : null,
    }));
  };

  const handleMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, message: event.target.value }));
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<CancelOutlined />} />}
        actionsRight={
          <>
            <Button variant="outlined" onClick={onClose}>
              {t('cancelButton')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleApply}>
              {t('applyButton')}
            </Button>
          </>
        }
      />
      <WidgetContent>
        <form noValidate autoComplete="off">
          <Grid container direction="row" spacing={3}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <TextField
                {...commonInputProps}
                select
                value={formData.reason ?? ''}
                label={t('reason.label')}
                onChange={handleReasonChange}
              >
                <MenuItem value="">{t('reason.none')}</MenuItem>
                {REASON_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {tCommon(`CaseCancellationReason.${option}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                {...commonInputProps}
                multiline
                rows={10}
                value={formData.message ?? ''}
                label={t('message.label')}
                onChange={handleMessageChange}
              />
            </Grid>
          </Grid>
        </form>
      </WidgetContent>
      {loading ? (
        <Cover>
          <CircularProgress />
        </Cover>
      ) : null}
    </Dialog>
  );
};
